/* eslint-disable indent */
import { PlayCircleOutlined, CodeOutlined, ClockCircleOutlined } from "@ant-design/icons";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Switch,
  Card,
  Tag,
  Typography,
  theme
} from "antd";
import dayjs from "dayjs";

import { CONTEST_DIVISION_TYPES, CONTEST_FORM_MODES, CONTEST_PLATFORM_TYPES } from "../helper.js";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const ContestFormModal = ({
  mode = CONTEST_FORM_MODES.UPDATE,
  isVisible = false,
  onCancel = () => {},
  onSave = () => {},
  contest = {},
  isLoading = false
}) => {
  const [formInstance] = Form.useForm();
  const { token } = theme.useToken();

  const handleSave = async () => {
    try {
      const formValues = await formInstance.validateFields();
      const payload = {
        contestIdentifier: formValues.contestIdentifier,
        name: formValues.name,
        platform:
          formValues.platform === CONTEST_PLATFORM_TYPES.OTHER
            ? formValues.customPlatform?.trim()
            : formValues.platform,
        contestDivisionTypes: formValues.contestDivisionTypes || [],
        startDate: formValues.dateRange[0],
        endDate: formValues.dateRange[1],
        duration: formValues.dateRange[1].diff(formValues.dateRange[0], "seconds"),
        link: formValues.link,
        solutionVideoLink: formValues.solutionVideoLink || "",
        updatedByAdmin: formValues.updatedByAdmin
      };
      onSave(payload.contestIdentifier, payload);
    } catch (error) {
      console.error("Validation Failed:", error);
    }
  };

  const initialValues = {
    contestIdentifier: contest?.contestIdentifier || "",
    name: contest?.name || "",
    platform: contest?.platform || "",
    dateRange:
      contest?.startDate && contest?.endDate
        ? [dayjs(contest?.startDate), dayjs(contest?.endDate)]
        : [],
    link: contest?.link || "",
    contestDivisionTypes: contest?.contestDivisionTypes || [],
    updatedByAdmin: contest?.updatedByAdmin || false,
    solutionVideoLink: contest?.solutionVideoInfo?.link || ""
  };

  const problemSolutions = contest?.solutionVideoInfo?.problemSolutions || [];
  const renderProblemSolutions = () => {
    if (problemSolutions.length === 0) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "40px 20px",
            backgroundColor: token.colorFillTertiary,
            borderRadius: "8px",
            border: `2px dashed ${token.colorBorder}`
          }}
        >
          <PlayCircleOutlined
            style={{ fontSize: "48px", color: token.colorTextSecondary, marginBottom: "16px" }}
          />
          <div style={{ color: token.colorTextSecondary, fontSize: "16px", fontWeight: "500" }}>
            No Problem Solutions Available
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {problemSolutions.map((solution, index) => (
          <Card
            key={index}
            size="small"
            style={{
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              border: `1px solid ${token.colorBorder}`
            }}
            bodyStyle={{ padding: "16px" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: "12px"
                }}
              >
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <Text strong style={{ fontSize: "16px" }}>
                    {solution.title}
                  </Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <Tag icon={<ClockCircleOutlined />} color="blue">
                    {solution.timestamp}
                  </Tag>
                  {solution.codeLink && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<CodeOutlined />}
                      href={solution.codeLink}
                      target="_blank"
                    >
                      View Code
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <Modal
      title={
        mode === CONTEST_FORM_MODES.UPDATE
          ? `Edit Contest: ${contest?.name || ""}`
          : "Create Contest"
      }
      open={isVisible}
      onCancel={onCancel}
      onOk={handleSave}
      confirmLoading={isLoading}
      width={900}
      destroyOnClose
      centered
    >
      <Form form={formInstance} layout="vertical" initialValues={initialValues}>
        <Form.Item name="contestIdentifier" label="Contest Identifier" rules={[{ required: true }]}>
          <Input disabled={mode === CONTEST_FORM_MODES.UPDATE} />
        </Form.Item>

        <Form.Item name="name" label="Contest Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item name="platform" label="Platform" rules={[{ required: true }]}>
          <Select
            onChange={(value) => {
              if (value !== CONTEST_PLATFORM_TYPES.OTHER)
                formInstance.setFieldsValue({ customPlatform: "" });
            }}
          >
            {Object.values(CONTEST_PLATFORM_TYPES).map((platformKey) => (
              <Option key={platformKey} value={platformKey}>
                {platformKey.charAt(0).toUpperCase() + platformKey.slice(1)}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.platform !== curr.platform}>
          {({ getFieldValue }) =>
            getFieldValue("platform") === CONTEST_PLATFORM_TYPES.OTHER && (
              <Form.Item
                name="customPlatform"
                label="Custom Platform Name"
                rules={[{ required: true, message: "Please enter platform name" }]}
              >
                <Input placeholder="Enter custom platform name" />
              </Form.Item>
            )
          }
        </Form.Item>

        <Form.Item name="dateRange" label="Contest Duration" rules={[{ required: true }]}>
          <RangePicker showTime format="DD MMM YYYY, HH:mm" style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="link" label="Contest Link" rules={[{ required: true }, { type: "url" }]}>
          <Input />
        </Form.Item>

        <Form.Item name="contestDivisionTypes" label="Division Types">
          <Select mode="tags">
            {CONTEST_DIVISION_TYPES.map((divisionType) => (
              <Option key={divisionType} value={divisionType}>
                {divisionType}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="updatedByAdmin" label="Updated by Admin" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item name="solutionVideoLink" label="YouTube Video Link" rules={[{ type: "url" }]}>
          <Input />
        </Form.Item>

        {mode === CONTEST_FORM_MODES.UPDATE && (
          <Form.Item label="Problem Solutions (Read Only)">
            <div
              style={{
                border: `1px solid ${token.colorBorder}`,
                borderRadius: "8px",
                padding: "20px",
                backgroundColor: token.colorFillTertiary,
                maxHeight: "300px",
                overflowY: "auto"
              }}
            >
              {renderProblemSolutions()}
            </div>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default ContestFormModal;
